import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Offices + embedded Google Maps — Cymmetri-themed equivalent of the Kathreftis
// kOffices block. Renders one card per office (label, name, address, "Get
// directions" link) beside a lazy-loaded map iframe. Defaults carry Cymmetri's
// real Head Office (Mumbai) and Development Center (Nagpur).
export const officeMapSchema = z.object({
  eyebrow: z.string().default('Our offices'),
  heading: z.string().default('Where To Find Us'),
  offices: z
    .array(
      z.object({
        label: z.string(),
        name: z.string().default('Cymmetri'),
        address: z.string(),
        // Free-text place/address used for the map embed + directions when no
        // explicit directionsUrl is given.
        mapQuery: z.string().default(''),
        directionsUrl: z.string().default(''),
      }),
    )
    .default([
      {
        label: 'Head Office',
        name: 'Cymmetri',
        address:
          'Unit No 905, 9th Floor, The Summit Business Park, Behind Guru Nanak Petrol Pump, Opp. Cinemax Cinema, Prakashwadi, Andheri (East), Mumbai 400093',
        mapQuery: 'The Summit Business Park, Andheri East, Mumbai 400093',
        directionsUrl: '',
      },
      {
        label: 'Development Center',
        name: 'Cymmetri',
        address:
          'Eureka Coworking, Plot No 11, 2nd & 4th Floor, Pratap Nagar Road, Mate Square, Nagpur 440022',
        mapQuery: 'Eureka Coworking, Pratap Nagar Road, Mate Square, Nagpur 440022',
        directionsUrl: '',
      },
    ]),
});

type OfficeMapProps = z.infer<typeof officeMapSchema>;

// Metrics mirror the Kathreftis kOffices widget (kathreftis.css .k-office*),
// rendered in Cymmetri's blue palette instead of teal.
const Section = styled.section`
  max-width: 120rem;
  margin: 0 auto;
  padding: 2rem 1.6rem 8rem;
`;

const Head = styled.div`
  max-width: 64rem;
  margin: 0 auto 4rem;
  text-align: center;
`;

const Eyebrow = styled.p`
  margin: 0 0 1rem;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const Heading = styled.h2`
  margin: 0;
  color: #0b1020;
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: clamp(2.6rem, 3.4vw, 3.6rem);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.6rem;
`;

const Office = styled.div`
  display: grid;
  gap: 0;
  border: 1px solid #e2e8f0;
  background: #ffffff;

  @media (min-width: 56em) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
    align-items: stretch;
  }
`;

const Body = styled.div`
  padding: 3.4rem 3.6rem;
`;

const Label = styled.p`
  margin: 0 0 1rem;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const Name = styled.h3`
  margin: 0 0 1.2rem;
  color: #0b1020;
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 2.1rem;
`;

const Address = styled.p`
  margin: 0 0 1.8rem;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  line-height: 1.7;
`;

const Directions = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const MapFrame = styled.iframe`
  width: 100%;
  min-height: 28rem;
  height: 100%;
  border: 0;
  display: block;
`;

// Build a same-origin-safe Google Maps embed URL. If the author already pasted a
// full /maps/embed URL, use it verbatim; otherwise build a query embed.
const mapSrc = (q: string) =>
  q.startsWith('https://www.google.com/maps/embed')
    ? q
    : `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;

function OfficeMap({ eyebrow, heading, offices }: OfficeMapProps) {
  return (
    <Section>
      {eyebrow || heading ? (
        <Head>
          {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
          {heading ? <Heading>{heading}</Heading> : null}
        </Head>
      ) : null}
      <List>
        {offices.map((o, i) => {
          const query = o.mapQuery || o.address;
          const directions =
            o.directionsUrl ||
            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
          return (
            <Office key={`${o.label}-${i}`}>
              <Body>
                <Label>{o.label}</Label>
                <Name>{o.name}</Name>
                <Address>{o.address}</Address>
                <Directions href={directions} target="_blank" rel="noopener noreferrer">
                  Get directions <span aria-hidden="true">↗</span>
                </Directions>
              </Body>
              <MapFrame
                src={mapSrc(query)}
                title={`${o.label} map`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </Office>
          );
        })}
      </List>
    </Section>
  );
}

export const officeMapBlock: BlockDefinition<OfficeMapProps> = {
  type: 'officeMap',
  schema: officeMapSchema,
  Renderer: OfficeMap,
};
